import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import * as Tooltip from '@radix-ui/react-tooltip'
import { Toaster } from 'sonner'
import ReaderLayout from './ReaderLayout'
import AdminLayout from './AdminLayout'
import DesignGuide from './components/conceptual/design-guide/DesignGuide'
import NYTDemo from './components/conceptual/NYTDemo'
import WireMockup from './WireMockup'
import FrontPage from './pages/FrontPage'
import ArticleViewPage from './pages/ArticleViewPage'
import MyWirePage from './pages/MyWirePage'
import SavedArticlesPage from './pages/SavedArticlesPage'
import ReadingHistoryPage from './pages/ReadingHistoryPage'
import LayoutCreatorPage from './pages/LayoutCreatorPage'
import LayoutListPage from './pages/LayoutListPage'
import PageListPage from './pages/PageListPage'
import PageEditorPage from './pages/PageEditorPage'
import NewPagePage from './pages/NewPagePage'
import NotFound from './pages/NotFound'
import DevStatusPage from './pages/DevStatusPage'
import ArticleListPage from './pages/ArticleListPage'
import ArticleReviewPage from './pages/ArticleReviewPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import PageViewPage from './pages/PageViewPage'
import LivePreviewPage from './pages/LivePreviewPage'
import LiveArticlePreviewPage from './pages/LiveArticlePreviewPage'
import LiveLayoutPreviewPage from './pages/LiveLayoutPreviewPage'
import AiSearchPage from './pages/AiSearchPage'
import ArticleSearchPage from './pages/ArticleSearchPage'
import MetricsPage from './pages/MetricsPage'
import TopicRegionPage from './pages/TopicRegionPage'
import CreateBookPage from './pages/CreateBookPage'
import BookPage from './pages/BookPage'
import SpecialEditionPage from './pages/SpecialEditionPage'
import { AuthGuard } from './auth/AuthGuard'
import { AdminGuard } from './auth/AdminGuard'
import { OidcCallback } from './auth/OidcCallback'
import { LoggedOut } from './auth/LoggedOut'

const router = createBrowserRouter([
  // OIDC callback and post-logout page — must be outside AuthGuard
  { path: '/callback', element: <OidcCallback /> },
  { path: '/logged-out', element: <LoggedOut /> },

  // Book creation wizard — full-screen, no navbar/footer chrome
  {
    path: '/wire/me/books/new',
    element: <AuthGuard><CreateBookPage /></AuthGuard>,
  },

  // Reader (protected)
  {
    element: <AuthGuard><ReaderLayout /></AuthGuard>,
    children: [
      { path: '/', element: <Navigate to="/wire" replace /> },
        { path: '/wire', element: <FrontPage /> },
        { path: '/wire/me', element: <MyWirePage /> },
        { path: '/wire/me/saved', element: <SavedArticlesPage /> },
        { path: '/wire/me/history', element: <ReadingHistoryPage /> },
        { path: '/wire/classic', element: <WireMockup /> },
        { path: '/wire/articles/:id', element: <ArticleViewPage /> },
        { path: '/wire/pages/:id', element: <PageViewPage /> },
        { path: '/wire/search', element: <AiSearchPage /> },
        { path: '/wire/search-results', element: <ArticleSearchPage /> },
        { path: '/wire/topics/:slug', element: <TopicRegionPage kind="topic" /> },
        { path: '/wire/regions/:slug', element: <TopicRegionPage kind="region" /> },
        { path: '/wire/editions/:slug', element: <SpecialEditionPage /> },
        { path: '/wire/books/:id', element: <BookPage /> },
        { path: '/nyt', element: <NYTDemo /> },
    ],
  },

  // Standalone live-preview windows — protected but no AdminLayout chrome
  {
    path: '/admin/pages/:id/live-preview',
    element: <AdminGuard><LivePreviewPage /></AdminGuard>,
  },
  {
    path: '/admin/articles/:id/live-preview',
    element: <AdminGuard><LiveArticlePreviewPage /></AdminGuard>,
  },
  {
    path: '/admin/layouts/:id/live-preview',
    element: <AdminGuard><LiveLayoutPreviewPage /></AdminGuard>,
  },

  // Admin (protected)
  {
    path: '/admin',
    element: <AdminGuard><AdminLayout /></AdminGuard>,
    children: [
      { index: true, element: <AdminDashboardPage /> },
      {
        path: 'layouts',
        children: [
          { index: true, element: <LayoutListPage /> },
          { path: 'new', element: <LayoutCreatorPage /> },
          { path: ':id', element: <LayoutCreatorPage /> },
        ],
      },
      {
        path: 'pages',
        children: [
          { index: true, element: <PageListPage /> },
          { path: 'new', element: <NewPagePage /> },
          { path: ':id', element: <PageEditorPage /> },
        ],
      },
      {
        path: 'articles',
        children: [
          { index: true, element: <ArticleListPage /> },
          { path: ':id', element: <ArticleReviewPage /> },
        ],
      },
      { path: 'design-guide', element: <DesignGuide /> },
      { path: 'dev-status', element: <DevStatusPage /> },
      { path: 'metrics', element: <MetricsPage /> },
    ],
  },

  { path: '*', element: <NotFound /> },
])

function App() {
  return (
    <Tooltip.Provider delayDuration={400}>
      <RouterProvider router={router} />
      <Toaster position="bottom-right" richColors />
    </Tooltip.Provider>
  )
}

export default App
